import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import { ToolbarButton } from '@atlaskit/editor-toolbar/toolbar-button';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import { ToolbarTooltip } from '@atlaskit/editor-toolbar/toolbar-tooltip';
import { expValEquals } from '@confluence/feature-experiments';

type RemixDisabledReason = 'archived' | 'version-history';

const messages = defineMessages({
	remixInProgressTooltip: {
		id: 'highlight-actions.remix-action.in-progress-tooltip',
		defaultMessage: 'Remix in progress...',
		description:
			'Tooltip shown on the disabled Remix button when a remix session is already in progress',
	},
	createInProgressTooltip: {
		id: 'highlight-actions.remix-action.create-in-progress-tooltip',
		defaultMessage: 'Create in progress...',
		description:
			'Tooltip shown on the disabled Create button when a create session is already in progress',
	},
	remixToolbarTooltip: {
		id: 'highlight-actions.remix-action.toolbar-tooltip',
		defaultMessage: 'Remix',
		description:
			'Tooltip for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	remixToolbarLabel: {
		id: 'highlight-actions.remix-action.toolbar-label',
		defaultMessage: 'Remix',
		description:
			'Label for the Remix button in the view page highlight actions inline toolbar that opens a modal to remix or regenerate the block content',
	},
	createToolbarTooltip: {
		id: 'highlight-actions.remix-action.create-toolbar-tooltip',
		defaultMessage: 'Create',
		description:
			'Tooltip for the Create button in the view page highlight actions inline toolbar that opens a modal to create new content from the block content',
	},
	createToolbarLabel: {
		id: 'highlight-actions.remix-action.create-toolbar-label',
		defaultMessage: 'Create',
		description:
			'Label for the Create button in the view page highlight actions inline toolbar that opens a modal to create new content from the block content',
	},
	remixDisabledTooltipArchived: {
		id: 'highlight-actions.remix-action.disabled-tooltip-archived',
		defaultMessage: "Content on archived pages can't be remixed",
		description: 'Tooltip shown on the disabled Remix button when the page is archived',
	},
	remixDisabledTooltipVersionHistory: {
		id: 'highlight-actions.remix-action.disabled-tooltip-version-history',
		defaultMessage: 'Remix is unavailable in version history',
		description:
			'Tooltip shown on the disabled Remix button when viewing a previous version of the page',
	},
});

const getRemixDisabledTooltip = (
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
	reason?: RemixDisabledReason,
): string => {
	switch (reason) {
		case 'version-history':
			return formatMessage(messages.remixDisabledTooltipVersionHistory);
		case 'archived':
		default:
			return formatMessage(messages.remixDisabledTooltipArchived);
	}
};

export const RemixAction = ({
	isNewToolbarEnabled,
	onClick,
	isDisabled,
	isRemixInProgress,
	remixDisabledReason,
}: {
	isNewToolbarEnabled?: boolean;
	onClick?: () => void;
	isDisabled?: boolean;
	isRemixInProgress?: boolean;
	remixDisabledReason?: RemixDisabledReason;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	// A/A experiment paired with cc_maui_create_keyword; called to balance exposures. Result unused.
	expValEquals('cc_maui_create_keyword_aa', 'isEnabled', true);

	// cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create" across the button.
	const labelMessage = expValEquals('cc_maui_create_keyword', 'isEnabled', true)
		? messages.createToolbarLabel
		: messages.remixToolbarLabel;

	let tooltipContent: string;
	if (isRemixInProgress) {
		tooltipContent = formatMessage(
			expValEquals('cc_maui_create_keyword', 'isEnabled', true)
				? messages.createInProgressTooltip
				: messages.remixInProgressTooltip,
		);
	} else if (isDisabled) {
		tooltipContent = getRemixDisabledTooltip(formatMessage, remixDisabledReason);
	} else {
		tooltipContent = formatMessage(
			expValEquals('cc_maui_create_keyword', 'isEnabled', true)
				? messages.createToolbarTooltip
				: messages.remixToolbarTooltip,
		);
	}

	return isNewToolbarEnabled ? (
		<ToolbarTooltip content={tooltipContent}>
			<ToolbarButtonGroup>
				<ToolbarButton
					iconBefore={<AiGenerativeRemixIcon label="" size="small" />}
					testId="highlight-actions-remix"
					onClick={onClick}
					isDisabled={isDisabled || isRemixInProgress}
				>
					<FormattedMessage {...labelMessage} />
				</ToolbarButton>
			</ToolbarButtonGroup>
		</ToolbarTooltip>
	) : (
		<ToolbarTooltip content={tooltipContent}>
			<Button
				appearance="subtle"
				iconBefore={AiGenerativeRemixIcon}
				testId="highlight-actions-remix"
				onClick={onClick}
				isDisabled={isDisabled || isRemixInProgress}
			>
				<FormattedMessage {...labelMessage} />
			</Button>
		</ToolbarTooltip>
	);
};
